/**
 * Espace Interventions (17/10/2026) : les onglets, ce que chacun demande comme
 * droit, l'onglet ouvert par défaut et les filtres lus dans l'adresse. Logique
 * pure, testée sans rendu (tests/unit/interventions-list.test.ts) ; le serveur
 * (`GET /interventions`, `planning/backlog.ts`) décide toujours de ce qu'on voit.
 */
import { INTERVENTION_TYPES } from '@myequip/shared';

export const INTERVENTION_TABS = [
  'demandes', 'a_planifier', 'planifiees', 'en_cours', 'a_cloturer', 'a_facturer', 'terminees', 'archivees',
] as const;
export type InterventionTab = (typeof INTERVENTION_TABS)[number];

/** Onglets des interventions (tout sauf les demandes), tels que l'API les nomme. */
export type BacklogTab = Exclude<InterventionTab, 'demandes'>;

/** Droit sans lequel un onglet n'est pas proposé (l'API répondrait 403). */
const TAB_RIGHTS: Partial<Record<InterventionTab, string>> = {
  demandes: 'intervention.create_any',
  a_facturer: 'invoice.view',
  archivees: 'intervention.cancel',
};

/**
 * Droits qui donnent à lire le planning (donc les interventions planifiées),
 * y compris la lecture seule de l'équipe (l'apprenti : `view_peers`).
 * Sans eux (une comptabilité, par exemple) seul l'onglet « À facturer »
 * reste, s'ils voient les factures : les autres seraient toujours vides.
 */
export const PLANNING_READ_RIGHTS = ['planning.view_all', 'planning.edit_all', 'planning.edit_own', 'planning.view_peers'] as const;

export function canReadPlanning(can: (capability: string) => boolean): boolean {
  return PLANNING_READ_RIGHTS.some((right) => can(right));
}

/** Les onglets que ce compte peut ouvrir ; vide : l'espace lui est fermé. */
export function tabsFor(can: (capability: string) => boolean): InterventionTab[] {
  const planning = canReadPlanning(can);
  return INTERVENTION_TABS.filter((tab) => {
    const right = TAB_RIGHTS[tab];
    if (right && !can(right)) return false;
    return planning || tab === 'a_facturer';
  });
}

/**
 * Onglet ouvert sans `onglet` dans l'adresse : les demandes pour qui les
 * qualifie (le client attend, c'est l'entrée de tout le reste), les
 * interventions planifiées pour qui lit le planning, sinon « À facturer ».
 */
export function defaultTab(can: (capability: string) => boolean): InterventionTab {
  if (can('intervention.create_any')) return 'demandes';
  return canReadPlanning(can) ? 'planifiees' : 'a_facturer';
}

/** L'onglet demandé s'il est permis, sinon l'onglet par défaut. */
export function resolveTab(value: string | null | undefined, can: (capability: string) => boolean): InterventionTab {
  const allowed = tabsFor(can);
  return allowed.includes(value as InterventionTab) ? (value as InterventionTab) : defaultTab(can);
}

export const PERIODS = ['', 'aujourdhui', 'semaine', 'mois'] as const;
export type Period = (typeof PERIODS)[number];

export const PERIOD_LABELS: Record<Period, string> = {
  '': 'Toutes les dates',
  aujourdhui: 'Aujourd’hui',
  semaine: 'Cette semaine',
  mois: 'Ce mois-ci',
};

/**
 * Bornes d'une période de calendrier autour de `now` (heure locale) : du début
 * du jour, de la semaine (lundi) ou du mois, jusqu'au début du suivant.
 */
export function periodRange(period: Period, now = new Date()): { from: string; to: string } | null {
  if (!period) return null;
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  let end: Date;
  if (period === 'aujourdhui') {
    end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 1);
  } else if (period === 'semaine') {
    const monday = (start.getDay() + 6) % 7;
    start.setDate(start.getDate() - monday);
    end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
  } else {
    start.setDate(1);
    end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  }
  return { from: start.toISOString(), to: end.toISOString() };
}

/** Valeur du filtre technicien pour « personne ». */
export const UNASSIGNED = 'aucun';
export const PRIORITIES = ['', 'urgente', 'normale'] as const;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export interface ListFiltersValue {
  technicien: string;
  type: string;
  priorite: string;
  periode: Period;
}

export const NO_FILTERS: ListFiltersValue = { technicien: '', type: '', priorite: '', periode: '' };

/** Une adresse modifiée à la main ne filtre jamais sur une valeur invalide. */
export function readFilters(get: (name: keyof ListFiltersValue) => string | null): ListFiltersValue {
  const technicien = get('technicien') ?? '';
  const type = get('type') ?? '';
  const priorite = get('priorite') ?? '';
  const periode = get('periode') ?? '';
  return {
    technicien: technicien === UNASSIGNED || UUID.test(technicien) ? technicien : '',
    type: (INTERVENTION_TYPES as readonly string[]).includes(type) ? type : '',
    priorite: (PRIORITIES as readonly string[]).includes(priorite) ? priorite : '',
    periode: (PERIODS as readonly string[]).includes(periode) ? (periode as Period) : '',
  };
}

export function activeFilterCount(filters: ListFiltersValue): number {
  return Object.values(filters).filter(Boolean).length;
}

/** Paramètres de l'API pour ces filtres et cette recherche. */
export function apiFilters(q: string, filters: ListFiltersValue, now = new Date()): Record<string, string | undefined> {
  const range = periodRange(filters.periode, now);
  return {
    q: q.trim() || undefined,
    assigneeId: filters.technicien || undefined,
    type: filters.type || undefined,
    priority: filters.priorite || undefined,
    from: range?.from,
    to: range?.to,
  };
}
